/**
 * ScreenTimeRules.js - Parent dashboard for managing screen time rules (Android Only)
 *
 * Features:
 * - Master toggle for all rules
 * - Active rules list grouped by child
 * - Manual lock/unlock per child
 * - Emergency contacts editor
 * - Notification preferences
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Badge,
  Avatar,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  Lock,
  LockOpen,
  Phone,
  PhoneInTalk,
  Warning,
  Schedule,
  Delete,
  NotificationsActive,
  Sms,
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
// import { useAuth } from '../contexts/AuthContext';
import { useFamilyId } from '../contexts/FamilyIdContext';
import { useFamily } from '../contexts/FamilyContext';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { buildScreenTimeRule } from '../services/RuleScheduler';
import { useAllScreenTimeRules } from '../hooks/useScreenTimeRules';

const DURATION_OPTIONS = [
  { label: '15 minutes', minutes: 15 },
  { label: '30 minutes', minutes: 30 },
  { label: '1 hour', minutes: 60 },
  { label: '2 hours', minutes: 120 },
  { label: 'Indefinite', minutes: null },
];

function ScreenTimeRules() {
  const navigate = useNavigate();
  // const { currentUser } = useAuth();
  const { familyId } = useFamilyId();
  const { familyMembers } = useFamily();

  const { rules, loading: rulesLoading } = useAllScreenTimeRules(familyId);

  const [masterEnabled, setMasterEnabled] = useState(true);
  const [masterLoading, setMasterLoading] = useState(false);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockTarget, setLockTarget] = useState(null); // { id, name }
  const [lockDuration, setLockDuration] = useState(DURATION_OPTIONS[2]); // 1 hour default
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Emergency contacts
  const [momPhone, setMomPhone] = useState('');
  const [dadPhone, setDadPhone] = useState('');
  const [contactsSaving, setContactsSaving] = useState(false);

  // Notification preferences
  const [notifPush, setNotifPush] = useState(true);
  const [notifSms, setNotifSms] = useState(false);
  const [notifPhone, setNotifPhone] = useState('');
  const [notifSaving, setNotifSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const children = familyMembers.filter(m => m.role === 'child');
  // const parents = familyMembers.filter(m => m.role === 'parent');

  useEffect(() => {
    if (familyId) {
      loadSettings();
      // Show onboarding for first-time users
      const seen = localStorage.getItem('earn_e_screentime_onboarding');
      if (!seen) setShowOnboarding(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId]);

  /**
   * Determine if a child is currently locked based on active rules
   */
  const isChildCurrentlyLocked = (childId) => {
    if (!masterEnabled) return false;
    const now = new Date();
    const childRules = getRulesForChild(childId);
    const DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    return childRules.some(rule => {
      if (!rule.screenTimeEnabled) return false;
      const startTime = rule.startTime?.toDate?.() || (rule.startTime ? new Date(rule.startTime) : null);
      const endTime = rule.endTime?.toDate?.() || (rule.endTime ? new Date(rule.endTime) : null);
      if (rule.recurring && rule.recurringDays?.length > 0) {
        const todayName = DAY_NAMES[now.getDay()];
        if (rule.recurringDays.includes(todayName) && startTime && endTime) {
          const todayStart = new Date(); todayStart.setHours(startTime.getHours(), startTime.getMinutes(), 0);
          const todayEnd = new Date(); todayEnd.setHours(endTime.getHours(), endTime.getMinutes(), 0);
          return now >= todayStart && now <= todayEnd;
        }
      } else if (startTime && endTime) {
        return now >= startTime && now <= endTime;
      } else if (startTime && !endTime) {
        return now >= startTime;
      }
      return false;
    });
  };

  const loadSettings = async () => {
    try {
      // Load master toggle
      const settingsRef = doc(db, `families/${familyId}/settings/screenTime`);
      const settingsSnap = await getDoc(settingsRef);
      if (settingsSnap.exists()) {
        setMasterEnabled(settingsSnap.data().masterEnabled !== false);
      }

      // Load emergency contacts
      const momRef = doc(db, `families/${familyId}/emergencyContacts/mom`);
      const dadRef = doc(db, `families/${familyId}/emergencyContacts/dad`);
      const [momSnap, dadSnap] = await Promise.all([getDoc(momRef), getDoc(dadRef)]);
      if (momSnap.exists()) setMomPhone(momSnap.data().phone || '');
      if (dadSnap.exists()) setDadPhone(dadSnap.data().phone || '');

      // Load notification prefs
      const notifRef = doc(db, `families/${familyId}/settings/notifications`);
      const notifSnap = await getDoc(notifRef);
      if (notifSnap.exists()) {
        const data = notifSnap.data();
        setNotifPush(data.overdueChore?.push !== false);
        setNotifSms(data.overdueChore?.sms === true);
        setNotifPhone(data.overdueChore?.smsPhone || '');
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const handleMasterToggle = async (enabled) => {
    setMasterLoading(true);
    try {
      setMasterEnabled(enabled);
      await setDoc(
        doc(db, `families/${familyId}/settings/screenTime`),
        { masterEnabled: enabled, updatedAt: new Date().toISOString() },
        { merge: true }
      );
      showSnackbar(enabled ? 'All screen time rules enabled' : 'All screen time rules disabled', 'success');
    } catch (err) {
      console.error('Failed to toggle master:', err);
      setMasterEnabled(!enabled); // revert
      showSnackbar('Failed to update setting', 'error');
    } finally {
      setMasterLoading(false);
    }
  };

  const handleLockNow = (child) => {
    setLockTarget(child);
    setLockDialogOpen(true);
  };

  const handleConfirmLock = async () => {
    if (!lockTarget) return;
    setLockDialogOpen(false);

    try {
      const startTime = new Date();
      const endTime = lockDuration.minutes
        ? new Date(Date.now() + lockDuration.minutes * 60 * 1000)
        : null;

      const rule = buildScreenTimeRule({
        type: 'manual_lock',
        childId: lockTarget.id,
        startTime,
        endTime,
        unlockOnApprove: false,
        createdBy: 'parent',
      });

      await addDoc(collection(db, `families/${familyId}/screenTimeRules`), rule);
      showSnackbar(`${lockTarget.displayName || 'Child'}'s device locked (${lockDuration.label})`, 'success');
    } catch (err) {
      console.error('Lock now failed:', err);
      showSnackbar('Failed to lock device', 'error');
    }
  };

  const handleUnlockNow = async (childId, childName) => {
    try {
      // Delete all active manual_lock rules for this child
      const rulesRef = collection(db, `families/${familyId}/screenTimeRules`);
      const q = query(rulesRef, where('childId', '==', childId), where('type', '==', 'manual_lock'));
      const snap = await getDocs(q);
      for (const ruleDoc of snap.docs) {
        await deleteDoc(doc(db, `families/${familyId}/screenTimeRules/${ruleDoc.id}`));
      }
      showSnackbar(`${childName || 'Child'}'s device unlocked`, 'success');
    } catch (err) {
      console.error('Unlock failed:', err);
      showSnackbar('Failed to unlock device', 'error');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await deleteDoc(doc(db, `families/${familyId}/screenTimeRules/${ruleId}`));
      showSnackbar('Rule deleted', 'success');
    } catch (err) {
      console.error('Delete rule failed:', err);
      showSnackbar('Failed to delete rule', 'error');
    }
  };

  const handleSaveContacts = async () => {
    setContactsSaving(true);
    try {
      await Promise.all([
        setDoc(doc(db, `families/${familyId}/emergencyContacts/mom`), { phone: momPhone, updatedAt: new Date().toISOString() }),
        setDoc(doc(db, `families/${familyId}/emergencyContacts/dad`), { phone: dadPhone, updatedAt: new Date().toISOString() }),
      ]);
      showSnackbar('Emergency contacts saved', 'success');
    } catch (err) {
      console.error('Save contacts failed:', err);
      showSnackbar('Failed to save contacts', 'error');
    } finally {
      setContactsSaving(false);
    }
  };

  const handleSaveNotifPrefs = async () => {
    setNotifSaving(true);
    try {
      await setDoc(
        doc(db, `families/${familyId}/settings/notifications`),
        {
          overdueChore: {
            push: notifPush,
            sms: notifSms,
            smsPhone: notifPhone,
          },
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      showSnackbar('Notification preferences saved', 'success');
    } catch (err) {
      console.error('Save notif prefs failed:', err);
      showSnackbar('Failed to save preferences', 'error');
    } finally {
      setNotifSaving(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getRulesForChild = (childId) => rules.filter(r => r.childId === childId);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return d.toLocaleString();
  };

  const getRuleTypeLabel = (type) => {
    switch (type) {
      case 'chore': return '📋 Chore';
      case 'calendar_event': return '📅 Calendar Event';
      case 'manual_lock': return '🔒 Manual Lock';
      default: return type;
    }
  };

  return (
    <Box sx={{ pb: 10 }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/parent')} edge="start">
            <ArrowBack />
          </IconButton>
          <Box sx={{ ml: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              📱 Screen Time Rules
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Android Only
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 3 }}>

        {/* Android Only Notice */}
        <Alert severity="info" sx={{ mb: 3 }} icon={<Warning />}>
          <Typography variant="body2">
            <strong>Screen Time Rules (Android Only)</strong><br />
            These rules enforce screen time limits on Android devices. iOS support coming soon.
          </Typography>
        </Alert>

        {/* Active Now Summary */}
        {masterEnabled && children.some(c => isChildCurrentlyLocked(c.id)) && (
          <Alert severity="error" sx={{ mb: 3 }} icon={<Lock />}>
            <Typography variant="body2" fontWeight="bold">
              🔒 Currently Locked:&nbsp;
              {children.filter(c => isChildCurrentlyLocked(c.id)).map(c => c.displayName).join(', ')}
            </Typography>
            <Typography variant="caption">
              These devices are pinned to Earn-E right now.
            </Typography>
          </Alert>
        )}

        {/* Master Toggle */}
        <Card sx={{ mb: 3, border: masterEnabled ? '2px solid #4caf50' : '2px solid #ccc' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {masterEnabled ? <ToggleOn color="success" sx={{ mr: 1, verticalAlign: 'middle' }} /> : <ToggleOff color="disabled" sx={{ mr: 1, verticalAlign: 'middle' }} />}
                  Master Toggle
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {masterEnabled ? 'All screen time rules are ACTIVE' : 'All screen time rules are DISABLED'}
                </Typography>
              </Box>
              {masterLoading ? (
                <CircularProgress size={28} />
              ) : (
                <Switch
                  checked={masterEnabled}
                  onChange={(e) => handleMasterToggle(e.target.checked)}
                  color="success"
                  size="medium"
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Manual Lock Per Child */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <Lock sx={{ mr: 1, verticalAlign: 'middle' }} />
              Manual Lock / Unlock
            </Typography>
            {children.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No children in your family yet.</Typography>
            ) : (
              children.map(child => (
                <Box key={child.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Badge
                        variant="dot"
                        color={isChildCurrentlyLocked(child.id) ? 'error' : 'default'}
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      >
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.9rem', bgcolor: isChildCurrentlyLocked(child.id) ? 'error.light' : 'primary.main' }}>
                          {child.displayName?.charAt(0)}
                        </Avatar>
                      </Badge>
                      <Box>
                        <Typography fontWeight="bold">{child.displayName}</Typography>
                        {isChildCurrentlyLocked(child.id) && (
                          <Typography variant="caption" color="error.main" fontWeight="bold">
                            🔒 Locked Now
                          </Typography>
                        )}
                      </Box>
                      {getRulesForChild(child.id).length > 0 && !isChildCurrentlyLocked(child.id) && (
                        <Chip label="Rules Set" color="warning" size="small" icon={<Lock />} />
                      )}
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        color="error"
                        size="medium"
                        startIcon={<Lock />}
                        onClick={() => handleLockNow(child)}
                        disabled={!masterEnabled}
                        sx={{ minHeight: 48 }}
                      >
                        Lock Now
                      </Button>
                      <Button
                        variant="outlined"
                        color="success"
                        size="medium"
                        startIcon={<LockOpen />}
                        onClick={() => handleUnlockNow(child.id, child.displayName)}
                        sx={{ minHeight: 48 }}
                      >
                        Unlock Now
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </CardContent>
        </Card>

        {/* Active Rules List */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
              Active Rules
              {rules.length > 0 && <Chip label={rules.length} size="small" sx={{ ml: 1 }} />}
            </Typography>

            {rulesLoading ? (
              <CircularProgress size={24} />
            ) : rules.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No active screen time rules. Create chores or calendar events with screen time enabled.</Typography>
            ) : (
              children.map(child => {
                const childRules = getRulesForChild(child.id);
                if (childRules.length === 0) return null;
                return (
                  <Box key={child.id} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                      👦 {child.displayName}
                    </Typography>
                    {childRules.map(rule => (
                      <Box key={rule.id} sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Chip label={getRuleTypeLabel(rule.type)} size="small" sx={{ mb: 0.5 }} />
                            <Typography variant="caption" display="block" color="text.secondary">
                              Start: {formatTime(rule.startTime)}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              End: {rule.endTime ? formatTime(rule.endTime) : 'Indefinite'}
                            </Typography>
                            {rule.unlockOnApprove && (
                              <Typography variant="caption" color="primary" display="block">
                                Unlocks when chore is approved
                              </Typography>
                            )}
                          </Box>
                          <IconButton size="small" color="error" onClick={() => handleDeleteRule(rule.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Emergency Contacts */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <PhoneInTalk sx={{ mr: 1, verticalAlign: 'middle', color: 'error.main' }} />
              Emergency Contacts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These numbers appear as quick-call buttons when the device is locked. Always accessible.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="👩 Mom's Phone Number"
                  value={momPhone}
                  onChange={(e) => setMomPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  type="tel"
                  InputProps={{ startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="👨 Dad's Phone Number"
                  value={dadPhone}
                  onChange={(e) => setDadPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  type="tel"
                  InputProps={{ startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
            </Grid>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={handleSaveContacts}
              disabled={contactsSaving}
              startIcon={contactsSaving ? <CircularProgress size={16} /> : null}
            >
              Save Emergency Contacts
            </Button>
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <NotificationsActive sx={{ mr: 1, verticalAlign: 'middle' }} />
              Notification Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              How to notify you when a chore is overdue.
            </Typography>

            <FormControlLabel
              control={<Switch checked={notifPush} onChange={(e) => setNotifPush(e.target.checked)} color="primary" />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight="bold">🔔 Push Notification</Typography>
                  <Typography variant="caption" color="text.secondary">Notification sent to your device</Typography>
                </Box>
              }
              sx={{ mb: 2, display: 'flex' }}
            />

            <FormControlLabel
              control={<Switch checked={notifSms} onChange={(e) => setNotifSms(e.target.checked)} color="primary" />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight="bold"><Sms sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />SMS Text Message</Typography>
                  <Typography variant="caption" color="text.secondary">Text message to your phone</Typography>
                </Box>
              }
              sx={{ mb: 2, display: 'flex' }}
            />

            {notifSms && (
              <TextField
                fullWidth
                label="SMS Phone Number"
                value={notifPhone}
                onChange={(e) => setNotifPhone(e.target.value)}
                placeholder="+1 (555) 000-0000"
                type="tel"
                sx={{ mb: 2 }}
                helperText="Number to receive overdue chore SMS alerts"
              />
            )}

            <Button
              variant="outlined"
              onClick={handleSaveNotifPrefs}
              disabled={notifSaving}
              startIcon={notifSaving ? <CircularProgress size={16} /> : null}
            >
              Save Notification Preferences
            </Button>
          </CardContent>
        </Card>

      </Container>

      {/* Onboarding Dialog - first-time parent */}
      <Dialog open={showOnboarding} onClose={() => { setShowOnboarding(false); localStorage.setItem('earn_e_screentime_onboarding', '1'); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
          👋 Welcome to Screen Time Rules!
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Here's how screen time works in Earn-E:
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}><strong>📋 Chore Rules:</strong> When you create a chore with start/end times and enable screen time, the child's device locks during that window. It unlocks when you approve the chore.</Typography>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}><strong>📅 Calendar Rules:</strong> Calendar events can lock devices during the event (e.g., church, school, dinner time). Device auto-unlocks when the event ends.</Typography>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}><strong>🔒 Manual Lock:</strong> Lock a child's device instantly for any reason, for a set duration or indefinitely.</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2"><strong>🚨 Emergency Always Works:</strong> Even when locked, children can always call 911, Mom, and Dad. Set those numbers in Emergency Contacts below.</Typography>
          </Box>
          <Alert severity="warning" icon={false}>
            <strong>Android Only:</strong> App pinning requires an Android device. Parents are never locked.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => { setShowOnboarding(false); localStorage.setItem('earn_e_screentime_onboarding', '1'); }}>
            Got It! Let's Set Up
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lock Duration Dialog */}
      <Dialog open={lockDialogOpen} onClose={() => setLockDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Lock sx={{ mr: 1, verticalAlign: 'middle', color: 'error.main' }} />
          Lock {lockTarget?.displayName}'s Device
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select how long to lock the device to Earn-E app.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Lock Duration</InputLabel>
            <Select
              value={lockDuration.label}
              onChange={(e) => setLockDuration(DURATION_OPTIONS.find(d => d.label === e.target.value))}
              label="Lock Duration"
            >
              {DURATION_OPTIONS.map(opt => (
                <MenuItem key={opt.label} value={opt.label}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmLock} variant="contained" color="error" startIcon={<Lock />}>
            Lock Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ScreenTimeRules;
